import React, { Component } from "react";
import { connect } from "react-redux";
import { saveNewCategoryFilterSelection } from "../../redux/actions/categoryAction";
import { toggleHalfThePriceProducts } from "../../redux/actions/itemActions";
import { hideModal } from "../../redux/actions/modalActions";
import { saveNewSearch } from "../../redux/actions/searchActions";
import { resetCss } from "../../utils/modal";
import { searchHasValue } from "../../utils/searchFunctions";

interface Props {
  items: { 
    id: string; 
    name: string; 
    type?: string; 
    url?: string; 
    image?: string;
  }[];
  saveNewSearch: any;
  hideModal: any;
  search: SearchState;
  toggleHalfThePriceProducts: (
    isHalfThePriceClicked: boolean,
    callTheAPI: boolean
  ) => void;
  saveNewCategoryFilterSelection: (selectedNode: CategoryTreeNode | null) => {
    type: string;
    selectedNode: CategoryTreeNode | null;
  };
  query?: string;
}

class EnhancedSearchSuggestionsPopup extends Component<Props> {
  search = async (value: string) => {
    this.props.saveNewSearch(value);

    // Track the complete search term in Google Analytics when user clicks on a suggestion
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'search_complete', {
        search_term: value,
        search_type: 'suggestion_click'
      });
    }

    await this.props.toggleHalfThePriceProducts(false, false);

    if (!searchHasValue(this.props.search)) {
      await this.props.saveNewCategoryFilterSelection(null);
    }
    this.props.hideModal();
    resetCss();
  };

  // Group items by type for better organization
  groupItemsByType = () => {
    const { items } = this.props;
    const products = items.filter(item => item.type === 'product');
    const collections = items.filter(item => item.type === 'collection');
    const queries = items.filter(item => item.type === 'query');
    
    return { products, collections, queries };
  };

  // Highlight the matching part of the text
  highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return (
      <>
        {parts.map((part, i) => 
          regex.test(part) ? <span key={i} className="u-color-magenta u-semi-bold">{part}</span> : part
        )}
      </>
    );
  };

  render() {
    const { products, collections, queries } = this.groupItemsByType();
    const { query } = this.props;
    
    return (
      <div className="u-w-all">
        <div className="u-background--white u-br--small u-border--charcoal--100 u-shadow">
          {/* Products section */}
          {products.length > 0 && (
            <div className="u-border-bottom--charcoal--100">
              <div className="u-main-font--small u-color-darkgrey u-ph--16 u-pt--16 u-pb--8 u-semi-bold">
                Products
              </div>
              <div>
                {products.slice(0, 4).map((item) => (
                  <div
                    className="u-flex u-align-center u-w-all u-hoverover--darker-background u-cursor--pointer u-pv--12 u-ph--16"
                    key={item.id}
                    onClick={() => this.search(item.name)}
                  >
                    {item.image && (
                      <div className="u-mr--12" style={{ width: "32px", height: "32px", flexShrink: 0 }}>
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </div>
                    )}
                    <div className="u-flex-grow">
                      <div className="u-main-font--small">
                        {this.highlightMatch(item.name, query || '')}
                      </div>
                      <div className="u-main-font--vsmall u-color-darkgrey">
                        Product
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Collections section */}
          {collections.length > 0 && (
            <div className="u-border-bottom--charcoal--100">
              <div className="u-main-font--small u-color-darkgrey u-ph--16 u-pt--16 u-pb--8 u-semi-bold">
                Categories
              </div>
              <div>
                {collections.slice(0, 3).map((item) => (
                  <div
                    className="u-flex u-align-center u-w-all u-hoverover--darker-background u-cursor--pointer u-pv--12 u-ph--16"
                    key={item.id}
                    onClick={() => this.search(item.name)}
                  >
                    <div className="u-mr--12" style={{ width: "24px", height: "24px", flexShrink: 0 }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {/* Shopping basket icon */}
                        <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                        <path d="M3 6h18" />
                        <path d="M16 10a4 4 0 01-8 0" />
                      </svg>
                    </div>
                    <div className="u-main-font--small">
                      {this.highlightMatch(item.name, query || '')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Popular searches section */}
          {queries.length > 0 && (
            <div>
              <div className="u-main-font--small u-color-darkgrey u-ph--16 u-pt--16 u-pb--8 u-semi-bold">
                Popular Searches
              </div>
              <div className="u-flex u-flex-wrap u-ph--12 u-pb--16">
                {queries.slice(0, 5).map((item) => (
                  <div
                    className="u-cursor--pointer u-mr--8 u-mb--8 u-br--pill u-background--lightgrey u-pv--4 u-ph--12"
                    key={item.id}
                    onClick={() => this.search(item.name)}
                  >
                    <div className="u-main-font--small u-flex u-align-center">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" 
                        style={{ width: "14px", height: "14px", marginRight: "4px" }}>
                        <circle cx="11" cy="11" r="8" />
                        <path d="M21 21l-4.35-4.35" />
                      </svg>
                      {this.highlightMatch(item.name, query || '')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* No results message */}
          {products.length === 0 && collections.length === 0 && queries.length === 0 && (
            <div className="u-pv--24 u-ph--16 u-text-align--center u-color-darkgrey">
              <div className="u-mb--12">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" 
                  style={{ width: "48px", height: "48px", margin: "0 auto" }}>
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                  <path d="M8 11h6" />
                </svg>
              </div>
              <div className="u-main-font--medium">No results found</div>
              <div className="u-main-font--small u-mt--4">Try a different search term</div>
            </div>
          )}
          
          {/* View all results button */}
          {(products.length > 0 || collections.length > 0 || queries.length > 0) && (
            <div 
              className="u-cursor--pointer u-pv--12 u-ph--16 u-text-align--center u-background--lightgrey u-color-magenta u-semi-bold u-main-font--small"
              onClick={() => query && this.search(query)}
            >
              View all results
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  search: state.search,
});

export default connect(mapStateToProps, {
  saveNewSearch,
  hideModal,
  saveNewCategoryFilterSelection,
  toggleHalfThePriceProducts,
})(EnhancedSearchSuggestionsPopup);
